import {useQueryClient} from '@tanstack/react-query';
import {createApiQuery, useApiMutation} from './api';
import {
  CreateModelTestControlList,
  DeleteModelTestList,
  GetModelTestDefinition,
  GetModelTestFields,
  GetModelTestListResults,
  GetModelTestResults,
  RefreshModelTestControlList,
  UpdateModelTestLists,
} from '../../shared/api/definitions/modelTest';
import {ObjectId} from 'bson';

export const ModelTestFieldsCacheKey = 'modelTestFields';
export const ModelTestFieldsKeys = {
  all: [ModelTestFieldsCacheKey],
  market: (marketId: ObjectId) => [
    ...ModelTestFieldsKeys.all,
    marketId.toString(),
  ],
} as const;
export const useGetModelTestFields = createApiQuery(
  GetModelTestFields,
  ({marketId}) => ModelTestFieldsKeys.market(marketId)
);

export const ModelTestResultsCacheKey = 'modelTestResults';
export const ModelTestResultsKeys = {
  all: [ModelTestResultsCacheKey],
  market: (marketId: ObjectId) => [
    ...ModelTestResultsKeys.all,
    marketId.toString(),
  ],
} as const;
export const useGetModelTestResults = createApiQuery(
  GetModelTestResults,
  ({marketId}) => ModelTestResultsKeys.market(marketId)
);

export const ModelTestListResultsCacheKey = 'modelTestListResults';
export const ModelTestListResultKeys = {
  all: [ModelTestListResultsCacheKey],
  market: (marketId: ObjectId) => [
    ...ModelTestListResultKeys.all,
    marketId.toString(),
  ],
  list: ({listId, marketId}: {marketId: ObjectId; listId: ObjectId}) => [
    ...ModelTestListResultKeys.market(marketId),
    listId.toString(),
  ],
} as const;

export const useGetModelTestListResults = createApiQuery(
  GetModelTestListResults,
  ({listId, marketId}) => ModelTestListResultKeys.list({marketId, listId}),
  {
    staleTime: 1000 * 60 * 60, // keep data for an hour unless query is invalidated
  }
);

export const ModelTestDefinitionCacheKey = 'modelTestDefinition';
export const ModelTestDefinitionKeys = {
  all: [ModelTestDefinitionCacheKey],
  market: (marketId: ObjectId) => [
    ...ModelTestDefinitionKeys.all,
    marketId.toString(),
  ],
} as const;

export const useGetModelTestDefinition = createApiQuery(
  GetModelTestDefinition,
  ({marketId}) => ModelTestDefinitionKeys.market(marketId),
  {
    staleTime: 1000 * 60 * 60, // keep data for an hour unless query is invalidated
    refetchInterval: (query) =>
      query.state.data?.lists.some(({status}) => status.type === 'processing')
        ? 5_000
        : 0,
  }
);

export const useCreateModelTestControlList = () => {
  const queryClient = useQueryClient();

  return useApiMutation(CreateModelTestControlList, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ModelTestDefinitionKeys.all,
      });
    },
  });
};

export const useRefreshModelTestControlList = () => {
  const queryClient = useQueryClient();

  return useApiMutation(RefreshModelTestControlList, {
    onSuccess: async (_, {marketId, listId}) => {
      await queryClient.invalidateQueries({
        queryKey: ModelTestDefinitionKeys.market(marketId),
      });

      await queryClient.invalidateQueries({
        queryKey: ModelTestListResultKeys.list({
          marketId,
          listId,
        }),
      });
    },
  });
};

export const useDeleteModelTestList = () => {
  const queryClient = useQueryClient();

  return useApiMutation(DeleteModelTestList, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ModelTestDefinitionKeys.all,
      });
    },
  });
};

export const useUpdateModelTestLists = () => {
  const queryClient = useQueryClient();
  return useApiMutation(UpdateModelTestLists, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ModelTestDefinitionKeys.all,
      });
    },
  });
};
